import React, { useState, useRef } from "react";
import {
  Popper,
  Paper,
  Typography,
  Box,
  IconButton,
  ClickAwayListener,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { COLORS } from "../assets/theme";

interface CostBreakdownWithDialogProps {
  routingCost?: number;
  mnpCost?: number;
  hlrCost?: number;
  mnpProviderName?: string;
  hlrProviderName?: string;
  failureCause?: string;
  routingCostCurrency?: string;
  mnpCostCurrency?: string;
  hlrCostCurrency?: string;
  convertedRoutingCost?: number;
  convertedMnpCost?: number;
  convertedHlrCost?: number;
  convertedCostCurrency?: string;
}

const CostBreakdownWithDialog: React.FC<CostBreakdownWithDialogProps> = ({
  routingCost,
  mnpCost,
  hlrCost,
  mnpProviderName,
  hlrProviderName,
  failureCause,
  routingCostCurrency,
  mnpCostCurrency,
  hlrCostCurrency,
  convertedRoutingCost,
  convertedMnpCost,
  convertedHlrCost,
  convertedCostCurrency,
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const [showTimeout, setShowTimeout] = useState<NodeJS.Timeout | null>(null);

  const totalCost = (routingCost ?? 0) + (mnpCost ?? 0) + (hlrCost ?? 0);

  const hasConvertedCosts =
    convertedRoutingCost !== undefined ||
    convertedMnpCost !== undefined ||
    convertedHlrCost !== undefined;

  const convertedTotal = hasConvertedCosts
    ? (convertedRoutingCost ?? 0) +
      (convertedMnpCost ?? 0) +
      (convertedHlrCost ?? 0)
    : undefined;

  // Build a per-currency total map so we can display mixed-currency totals correctly
  const originalTotalsByCurrency: Record<string, number> = {};
  const addToCurrencyTotal = (cost: number | undefined, currency: string | undefined) => {
    if (cost === undefined || !currency) return;
    originalTotalsByCurrency[currency] = (originalTotalsByCurrency[currency] ?? 0) + cost;
  };
  addToCurrencyTotal(routingCost, routingCostCurrency);
  if (mnpCost !== undefined && mnpCost >= 0 && failureCause !== "NUMBER_INACTIVE") {
    addToCurrencyTotal(mnpCost, mnpCostCurrency);
  }
  if (hlrCost !== undefined && hlrCost >= 0) {
    addToCurrencyTotal(hlrCost, hlrCostCurrency);
  }
  const originalTotalEntries = Object.entries(originalTotalsByCurrency).filter(
    ([, amount]) => amount !== 0
  );

  const hasCurrencyInfo = Boolean(
    routingCostCurrency || mnpCostCurrency || hlrCostCurrency || convertedCostCurrency
  );

  const hasAdditionalCosts =
    (mnpCost !== undefined && mnpCost >= 0) ||
    (hlrCost !== undefined && hlrCost > 0) ||
    hasCurrencyInfo ||
    hasConvertedCosts;

  const handleMouseEnter = () => {
    if (!hasAdditionalCosts) return;

    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }

    if (showTimeout) {
      clearTimeout(showTimeout);
      setShowTimeout(null);
    }

    const timeout = setTimeout(() => {
      setOpen(true);
      setShowTimeout(null);
    }, 200);
    setShowTimeout(timeout);
  };

  const handleMouseLeave = () => {
    if (showTimeout) {
      clearTimeout(showTimeout);
      setShowTimeout(null);
    }

    const timeout = setTimeout(() => {
      setOpen(false);
    }, 300);
    setHoverTimeout(timeout);
  };

  const handlePopperMouseEnter = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    if (showTimeout) {
      clearTimeout(showTimeout);
      setShowTimeout(null);
    }
  };

  const handlePopperMouseLeave = () => {
    setOpen(false);
  };

  const handleClose = () => {
    if (showTimeout) {
      clearTimeout(showTimeout);
      setShowTimeout(null);
    }
    setOpen(false);
  };

  const handleClickAway = () => {
    if (showTimeout) {
      clearTimeout(showTimeout);
      setShowTimeout(null);
    }
    setOpen(false);
  };

  const formatCost = (cost: number) => {
    if (cost === 0) return "0";
    return cost.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 6,
    });
  };

  const triggerLabel =
    convertedTotal !== undefined
      ? convertedCostCurrency
        ? `${formatCost(convertedTotal)} ${convertedCostCurrency}`
        : formatCost(convertedTotal)
      : formatCost(totalCost);

  return (
    <>
      <Box
        ref={anchorRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          position: "relative",
          display: "inline-block",
          cursor: hasAdditionalCosts ? "pointer" : "default",
          color: COLORS.typographyColor,
          textDecoration: hasAdditionalCosts ? "underline" : "none",
          textDecorationStyle: hasAdditionalCosts ? "dotted" : "none",
          textUnderlineOffset: hasAdditionalCosts ? "3px" : "0",
        }}
      >
        <Typography variant="body2">{triggerLabel}</Typography>
      </Box>

      <Popper
        open={open && hasAdditionalCosts}
        anchorEl={anchorRef.current}
        placement="top-start"
        sx={{ zIndex: 1300 }}
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [0, 8],
            },
          },
        ]}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper
            onMouseEnter={handlePopperMouseEnter}
            onMouseLeave={handlePopperMouseLeave}
            sx={{
              p: 2,
              minWidth: 300,
              maxWidth: 440,
              boxShadow: 3,
              border: "1px solid #ccc",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1.5,
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                Cost Breakdown
              </Typography>
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{
                  color: (theme) => theme.palette.grey[500],
                  p: 0.5,
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {/* Routing Cost */}
              <CostRow
                label="Routing Cost"
                originalCost={routingCost}
                originalCurrency={routingCostCurrency}
                convertedCost={convertedRoutingCost}
                convertedCurrency={convertedCostCurrency}
                formatCost={formatCost}
              />

              {/* MNP Cost */}
              {mnpCost !== undefined &&
                mnpCost >= 0 &&
                failureCause !== "NUMBER_INACTIVE" && (
                  <CostRow
                    label={`MNP Cost${mnpCost === 0 ? " (cached)" : ""}`}
                    originalCost={mnpCost}
                    originalCurrency={mnpCostCurrency}
                    convertedCost={convertedMnpCost}
                    convertedCurrency={convertedCostCurrency}
                    providerName={mnpProviderName}
                    formatCost={formatCost}
                  />
                )}

              {/* HLR Cost */}
              {hlrCost !== undefined && hlrCost >= 0 && (
                <CostRow
                  label={`HLR Cost${hlrCost === 0 ? " (cached)" : ""}`}
                  originalCost={hlrCost}
                  originalCurrency={hlrCostCurrency}
                  convertedCost={convertedHlrCost}
                  convertedCurrency={convertedCostCurrency}
                  providerName={hlrProviderName}
                  formatCost={formatCost}
                />
              )}

              <Divider sx={{ my: 0.5 }} />

              {/* Total */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Typography variant="body2" fontWeight="bold">
                  Total Cost:
                </Typography>
                <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                  {originalTotalEntries.length > 0 ? (
                    <Typography variant="body2" fontWeight="bold">
                      {originalTotalEntries.map(([currency, amount], idx) => (
                        <React.Fragment key={currency}>
                          {idx > 0 && <span style={{ opacity: 0.5 }}>&nbsp;+&nbsp;</span>}
                          {formatCost(amount)}&nbsp;<span style={{ opacity: 0.7 }}>{currency}</span>
                        </React.Fragment>
                      ))}
                    </Typography>
                  ) : (
                    <Typography variant="body2" fontWeight="bold">
                      {formatCost(totalCost)}
                    </Typography>
                  )}
                  {convertedTotal !== undefined && (
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      ≈&nbsp;{formatCost(convertedTotal)}{convertedCostCurrency ? <>&nbsp;{convertedCostCurrency}</> : null}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

interface CostRowProps {
  label: string;
  originalCost?: number;
  originalCurrency?: string;
  convertedCost?: number;
  convertedCurrency?: string;
  providerName?: string;
  formatCost: (cost: number) => string;
}

const CostRow: React.FC<CostRowProps> = ({
  label,
  originalCost,
  originalCurrency,
  convertedCost,
  convertedCurrency,
  providerName,
  formatCost,
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 1,
    }}
  >
    <Box>
      <Typography variant="body2" color="text.secondary">
        {label}:
      </Typography>
      {providerName && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ pl: 1, fontStyle: "italic" }}
        >
          Provider: {providerName}
        </Typography>
      )}
    </Box>
    <Box sx={{ textAlign: "right", flexShrink: 0 }}>
      <Typography variant="body2" fontWeight="medium">
        {originalCost !== undefined ? (
          <>
            {formatCost(originalCost)}
            {originalCurrency ? <>&nbsp;<span style={{ opacity: 0.7 }}>{originalCurrency}</span></> : null}
          </>
        ) : (
          "-"
        )}
      </Typography>
      {originalCost !== undefined && convertedCost !== undefined && (
        <Typography variant="caption" color="text.secondary">
          ≈&nbsp;{formatCost(convertedCost)}{convertedCurrency ? <>&nbsp;{convertedCurrency}</> : null}
        </Typography>
      )}
    </Box>
  </Box>
);

export default CostBreakdownWithDialog;
